import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';

import {
  CONCENTRATION_RANKS,
  FIC_FIDC_COCKPIT_CONCENTRATION_VERSION,
} from './concentration/contract';
import {
  formatConcentrationCompetency,
  formatConcentrationCurrency,
  formatConcentrationPercentage,
} from './concentration/formatters';
import { getFicFidcCockpitConcentrationFeedback } from './concentration/statusMessages';
import { useFicFidcCockpitConcentration } from './concentration/useFicFidcCockpitConcentration';
import { useFollowingViewContext } from '../../Wallet/FollowingView/FollowingViewContext';

import type { FicFidcCockpitConcentrationRankEntry } from './concentration/mapFicFidcCockpitConcentration';

function getRankLabel(rank: number) {
  return `TOP ${rank} ${rank === 1 ? 'FIDC' : 'FIDCS'}`;
}

function getCardState(
  entry: FicFidcCockpitConcentrationRankEntry,
): 'available' | 'missing-position' | 'unavailable' {
  if (entry.isAvailable) return 'available';

  return entry.isMissingPosition ? 'missing-position' : 'unavailable';
}

function getCardAriaLabel(
  rank: number,
  entry: FicFidcCockpitConcentrationRankEntry,
): string {
  const label = getRankLabel(rank);

  if (entry.isAvailable && entry.percentage !== null && entry.value !== null) {
    return `${label}: ${formatConcentrationPercentage(entry.percentage)} do patrimônio do FIC, ${formatConcentrationCurrency(entry.value)}`;
  }

  if (entry.isMissingPosition) {
    return `${label}: N/D, a carteira não tem FIDC nesta posição`;
  }

  return `${label}: N/D, valor indisponível`;
}

function ConcentrationCard({
  entry,
}: {
  entry: FicFidcCockpitConcentrationRankEntry;
}) {
  const hasValue =
    entry.isAvailable && entry.value !== null && entry.percentage !== null;
  const label = getRankLabel(entry.rank);
  const cardState = getCardState(entry);

  return (
    <Card
      aria-label={getCardAriaLabel(entry.rank, entry)}
      data-highlighted={entry.rank === 1}
      data-state={cardState}
      data-testid={`fic-fidc-concentration-card-${entry.rank}`}
      variant="outlined"
      sx={(theme) => ({
        borderColor:
          entry.rank === 1 && hasValue ? theme.palette.primary.main : 'divider',
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        height: '100%',
      })}>
      <CardContent
        sx={{
          '&:last-child': { pb: 2 },
          minHeight: 116,
          p: { xs: 1.75, sm: 2 },
        }}>
        <Typography color="text.secondary" variant="caption">
          {label}
        </Typography>
        <Typography
          color={hasValue ? 'text.primary' : 'text.disabled'}
          fontWeight={700}
          sx={{ mt: 1 }}
          variant="h5">
          {hasValue && entry.percentage !== null
            ? formatConcentrationPercentage(entry.percentage)
            : 'N/D'}
        </Typography>
        <Typography
          color={hasValue ? 'text.secondary' : 'text.disabled'}
          sx={{ minHeight: 21 }}
          variant="body2">
          {hasValue && entry.value !== null
            ? `${formatConcentrationCurrency(entry.value)} do patrimônio do FIC`
            : 'Valor indisponível'}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ConcentrationCardSkeleton({ rank }: { rank: number }) {
  return (
    <Card
      aria-label={`${getRankLabel(rank)}: carregando`}
      data-testid={`fic-fidc-concentration-card-skeleton-${rank}`}
      variant="outlined"
      sx={(theme) => ({
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        height: '100%',
      })}>
      <CardContent
        sx={{
          '&:last-child': { pb: 2 },
          minHeight: 116,
          p: { xs: 1.75, sm: 2 },
        }}>
        <Skeleton height={18} width="45%" />
        <Skeleton height={34} sx={{ mt: 1 }} width="55%" />
        <Skeleton height={21} width="70%" />
      </CardContent>
    </Card>
  );
}

function getSectionSubtitle(
  data: ReturnType<typeof useFicFidcCockpitConcentration>['data'],
): string {
  const formattedCompetency = formatConcentrationCompetency(
    data?.competence ?? null,
  );

  if (!formattedCompetency || !data) {
    return 'Dados da competência selecionada';
  }

  if (data.totalFunds !== null && data.totalFunds > 0) {
    return `Competência ${formattedCompetency} · ${data.totalFunds} ${
      data.totalFunds === 1 ? 'FIDC elegível' : 'FIDCs elegíveis'
    }`;
  }

  return `Competência ${formattedCompetency}`;
}

export function FicFidcPortfolioConcentrationSection() {
  const { operationId, indicatorsCompetencyDate } = useFollowingViewContext();
  const { status, data } = useFicFidcCockpitConcentration(
    operationId,
    indicatorsCompetencyDate,
  );
  const feedback = getFicFidcCockpitConcentrationFeedback(
    status,
    data?.reason ?? null,
  );

  return (
    <Box
      id="fic-fidc-cockpit-portfolio-concentration"
      aria-busy={status === 'loading'}
      component="section"
      data-state={status}
      aria-labelledby="fic-fidc-portfolio-concentration-title"
      sx={{ mb: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          id="fic-fidc-portfolio-concentration-title"
          component="h2"
          fontWeight={700}
          variant="h6">
          Concentração da carteira do FIC
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Participação dos maiores FIDCs detidos pelo FIC sobre o patrimônio
          líquido do FIC.
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {getSectionSubtitle(data)}
        </Typography>
      </Box>

      {feedback && (
        <Alert
          data-testid="fic-fidc-concentration-feedback"
          role="status"
          severity={feedback.severity}
          sx={{ mb: 2 }}
          variant="outlined">
          {feedback.message}
        </Alert>
      )}

      <Grid container alignItems="stretch" spacing={2}>
        {status === 'loading' || !data
          ? CONCENTRATION_RANKS.map((rank) => (
              <Grid item key={rank} md={3} sm={6} xs={12}>
                <ConcentrationCardSkeleton rank={rank} />
              </Grid>
            ))
          : data.ranks.map((entry) => (
              <Grid item key={entry.rank} md={3} sm={6} xs={12}>
                <ConcentrationCard entry={entry} />
              </Grid>
            ))}
      </Grid>

      {/* Contrato v1 do resumo de concentração consumido por esta seção. */}
      <span hidden data-testid="fic-fidc-concentration-contract-version">
        {FIC_FIDC_COCKPIT_CONCENTRATION_VERSION}
      </span>
    </Box>
  );
}

export default FicFidcPortfolioConcentrationSection;
